import React, { createContext, useContext, useState, useCallback } from 'react';
import { useLanguage } from './LanguageContext';

/**
 * Toast notification system — alert() yerine slide-in bildirim.
 * Variant'lar: success / error / info / warning.
 *
 * Usage:
 *   const toast = useToast();
 *   toast.success('Kaydedildi');
 *   toast.error('Hata oluştu', { duration: 6000 });
 *   toast('Generic info', { variant: 'info' });
 *
 * Default duration: 4000 ms (success/info) / 6000 ms (error/warning).
 * Toaster max 5 bildirim aynı anda gösterir; eski olanlar otomatik kaybolur.
 */

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const remove = useCallback((id) => {
    setToasts(ts => ts.filter(t => t.id !== id));
  }, []);

  const push = useCallback((message, opts = {}) => {
    const id = Date.now() + Math.random();
    const variant = opts.variant || 'info';
    const duration = opts.duration ?? (variant === 'error' || variant === 'warning' ? 6000 : 4000);
    let scheduled = true;
    setToasts(prev => {
      // Duplicate guard: aynı message + variant ardışık ise atla (örn. ağ hatası 10 kez)
      const last = prev[prev.length - 1];
      if (last && last.message === message && last.variant === variant) {
        scheduled = false;
        return prev;
      }
      const newToast = { id, message, variant, duration };
      // Overflow guard: queue 5+ ise en eskisini DROP et
      if (prev.length >= 5) {
        return [...prev.slice(1), newToast];
      }
      return [...prev, newToast];
    });
    if (scheduled && duration > 0) {
      setTimeout(() => remove(id), duration);
    }
    return id;
  }, [remove]);

  const api = {
    push,
    success: (msg, opts) => push(msg, { ...opts, variant: 'success' }),
    error:   (msg, opts) => push(msg, { ...opts, variant: 'error' }),
    warning: (msg, opts) => push(msg, { ...opts, variant: 'warning' }),
    info:    (msg, opts) => push(msg, { ...opts, variant: 'info' }),
    remove,
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Toaster toasts={toasts} onRemove={remove} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}

const VARIANT_STYLES = {
  success: 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200',
  error:   'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200',
  warning: 'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200',
  info:    'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200',
};

const VARIANT_ICONS = {
  success: '✅',
  error: '❌',
  warning: '⚠️',
  info: 'ℹ️',
};

function Toaster({ toasts, onRemove }) {
  const { t } = useLanguage();
  if (toasts.length === 0) return null;
  return (
    <div
      className="fixed top-3 right-3 left-3 sm:left-auto sm:max-w-sm z-[150] flex flex-col gap-2 pointer-events-none"
      role="region"
      aria-label={t('tab_notifications')}
      aria-live="polite"
    >
      {toasts.map(toast => (
        <div
          key={toast.id}
          role={toast.variant === 'error' || toast.variant === 'warning' ? 'alert' : 'status'}
          className={`pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-xl border shadow-lg backdrop-blur-sm ${VARIANT_STYLES[toast.variant]} animate-toast-in`}
        >
          <span className="text-lg flex-shrink-0 leading-none mt-0.5" aria-hidden="true">
            {VARIANT_ICONS[toast.variant]}
          </span>
          <span className="flex-1 text-sm font-medium leading-snug break-words">
            {toast.message}
          </span>
          <button
            onClick={() => onRemove(toast.id)}
            className="flex-shrink-0 w-6 h-6 rounded-md hover:bg-black/5 dark:hover:bg-white/10 flex items-center justify-center text-current opacity-60 hover:opacity-100 transition-opacity"
            aria-label={t('close')}
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>
      ))}
    </div>
  );
}
